import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'umi';
import { Bubble } from 'tea-component';
import TagItem from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/TagItem';
import { DBI_TOPCHARTS } from '@/pages/intelligence/models/topcharts';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';

import './index.scss';

export enum ETagType {
  GAME_MODE = 'game_mode',
  STEAM_TAG = 'steam_tag',
  GAME_ENGINE = 'game_engine',
}

interface ITagList {
  tags: string[];
  type: ETagType | string;
  onTagClick: (type: string, tag: string) => void;
}

interface ITagLocaleTextMap {
  [key: string]: string;
}

function TagList({ tags, type, onTagClick }: ITagList) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleTags, setVisibleTags] = useState<string[]>(tags);
  const [overflowCount, setOverflowCount] = useState(0);

  const dispatch = useDispatch();

  // 获取对应 localeTextMap 公共状态的映射
  const selectMap: { [key: string]: string } = {
    [ETagType.STEAM_TAG]: 'steamTagsLocaleTextMap',
    [ETagType.GAME_MODE]: 'gameModeLocaleTextMap',
    [ETagType.GAME_ENGINE]: 'gameEngineLocaleTextMap',
  };

  const { tagLocaleTextMap } = useSelector(
    (state: any): { tagLocaleTextMap: ITagLocaleTextMap } => {
      const { [selectMap[type]]: tagLocaleTextMap } = state[DBI_TOPCHARTS] || {};
      return { tagLocaleTextMap };
    },
  );

  // 初始所有 Tag 渲染后计算 +n
  useEffect(() => {
    const calculateOverflow = () => {
      const container = containerRef.current;
      if (!container) return;

      const children = Array.from(container.children);
      const maxHeight = container.offsetHeight;

      let currentHeight = 0;
      let visibleCount = 0;

      // 计算显示的标签，包含 +n 的占位
      for (const child of children) {
        const childRect = child.getBoundingClientRect();
        currentHeight = childRect.bottom - container.getBoundingClientRect().top;

        // 如果当前标签已经超出最大高度, 终止循环
        if (currentHeight > maxHeight) {
          break;
        }

        visibleCount += 1;
      }

      const totalTags = tags.length;
      const tagsToShow = visibleCount > totalTags ? totalTags : visibleCount;
      const remainingCount = totalTags - tagsToShow;

      const overflowCount = remainingCount > 0 ? remainingCount : 0;
      setOverflowCount(overflowCount);
      setVisibleTags(tags.slice(0, tagsToShow));
    };

    calculateOverflow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tags)]);

  // +n 渲染后，避免 +n 元素溢出，监听处理
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new MutationObserver(() => {
      const children = Array.from(container.children);

      let currentHeight = 0;
      const maxHeight = container.offsetHeight;

      const lastChild = children[children.length - 1];
      const childRect = lastChild?.getBoundingClientRect();

      currentHeight = childRect.bottom - container.getBoundingClientRect().top;

      if (currentHeight > maxHeight) {
        setVisibleTags((prevTags) => {
          const currTags = prevTags.slice();
          currTags.pop();
          return currTags;
        });
        setOverflowCount((x) => Number(x) + 1);
      }
    });

    // 开始观察 container 子节点变化
    observer.observe(container, {
      childList: true, // 监听子节点变化
    });

    return () => observer.disconnect(); // 清理
  }, []);

  // +n Bubble 提示中的 Tags
  const resetTags = useMemo(() => {
    return tags.slice(-overflowCount);
  }, [tags, overflowCount]);

  // 点击 Tag
  const handleClick = (tag: string) => {
    onTagClick?.(type, tag);

    dispatch({
      type: `${FILTER_EXTEND_NAMESPACE}/saveIsClickSteamOrGameModeTag`,
      payload: true,
    });
  };

  return (
    <div className="dbi_tag_list_container" ref={containerRef}>
      {visibleTags.map((tag, index) => {
        return (
          <TagItem
            key={index}
            text={tagLocaleTextMap[tag] || tag}
            onClick={() => handleClick(tag)}
          />
        );
      })}
      {Boolean(overflowCount) && (
        <Bubble
          content={
            <div className="dbi_tag_list_container_more">
              {resetTags.map((tag, index) => (
                <TagItem
                  key={index}
                  onClick={() => handleClick(tag)}
                  text={tagLocaleTextMap[tag] || tag}
                />
              ))}
            </div>
          }
        >
          <div>
            <TagItem>+{overflowCount}</TagItem>
          </div>
        </Bubble>
      )}
    </div>
  );
}

export default TagList;
